import { useState } from "react";
import type { Constraints, HelperMode, Profile, Space } from "../data/schema";
import { Button, Chip, Screen } from "./ui";

const EQUIPMENT = ["ball", "cones", "goal", "wall", "gloves", "ladder", "bibs"];

const HELPER_LABEL: Record<HelperMode, string> = {
  solo: "On my own",
  helper: "Someone to serve",
  together: "Both of us",
};
const SPACE_LABEL: Record<Space, string> = {
  small: "Small (backyard)",
  half: "Half pitch",
  full: "Full pitch",
};

export function ConstraintsForm({
  profile,
  onBack,
  onGenerate,
}: {
  profile: Profile;
  onBack: () => void;
  onGenerate: (c: Constraints) => void;
}) {
  const [durationMin, setDuration] = useState<30 | 45 | 60>(60);
  const [helper, setHelper] = useState<HelperMode>("helper");
  const [space, setSpace] = useState<Space>("full");
  const [equipment, setEquipment] = useState<string[]>(["ball", "cones", "goal"]);

  const toggle = (item: string) =>
    setEquipment((e) =>
      e.includes(item) ? e.filter((x) => x !== item) : [...e, item]
    );

  return (
    <Screen
      title={`Today · ${profile.name}`}
      right={
        <button onClick={onBack} className="text-lg font-semibold text-[var(--color-pitch)]">
          Back
        </button>
      }
    >
      <p className="mb-5 text-lg text-neutral-600">
        A few quick things and I&apos;ll build a session.
      </p>

      <Section label="How long?">
        {[30, 45, 60].map((d) => (
          <Chip key={d} active={durationMin === d} onClick={() => setDuration(d as 30 | 45 | 60)}>
            {d} min
          </Chip>
        ))}
      </Section>

      <Section label="Who's here?">
        {(Object.keys(HELPER_LABEL) as HelperMode[]).map((h) => (
          <Chip key={h} active={helper === h} onClick={() => setHelper(h)}>
            {HELPER_LABEL[h]}
          </Chip>
        ))}
      </Section>
      {profile.position === "goalkeeper" && helper === "solo" && (
        <p className="-mt-3 mb-4 text-base text-amber-700">
          Heads up: most keeper drills need someone to serve or shoot. Solo sessions
          focus on footwork and handling you can do alone.
        </p>
      )}

      <Section label="How much space?">
        {(Object.keys(SPACE_LABEL) as Space[]).map((s) => (
          <Chip key={s} active={space === s} onClick={() => setSpace(s)}>
            {SPACE_LABEL[s]}
          </Chip>
        ))}
      </Section>

      <Section label="What have you got?">
        {EQUIPMENT.map((item) => (
          <Chip key={item} active={equipment.includes(item)} onClick={() => toggle(item)}>
            {item}
          </Chip>
        ))}
      </Section>

      <Button
        className="mt-4 w-full"
        onClick={() => onGenerate({ durationMin, helper, space, equipment })}
      >
        Create today&apos;s plan
      </Button>
    </Screen>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h2 className="h-display mb-3 text-2xl text-[var(--color-ink)]">{label}</h2>
      <div className="flex flex-wrap gap-2.5">{children}</div>
    </div>
  );
}
